import React, { useCallback, useEffect, useRef, useState } from 'react';
import DOMPurify from 'dompurify';
import {
  Bold,
  Code,
  Heading1,
  Heading2,
  Heading3,
  Image as ImageIcon,
  Italic,
  Link2,
  List,
  ListOrdered,
  Quote,
  Redo2,
  RemoveFormatting,
  Table as TableIcon,
  Underline,
  Undo2,
  Video,
} from 'lucide-react';
import { promptDialog } from '../../lib/confirmStore';
import { notifyToast } from '../../lib/toastStore';

/**
 * Shared rich text editor for every long-form field in the platform
 * (program overview, eligibility, terms & conditions, instructions,
 * processing notes, marketing content, training articles).
 *
 * Design notes
 * ------------
 *  • Zero new dependencies — DOMPurify is already used by the app, and the
 *    editor is a controlled `contenteditable` surface.
 *  • Output is sanitised on every change AND again on render, so stored
 *    content can never inject script/handlers into a portal.
 *  • The allow-list keeps the formatting the brief asks for: headings, bold,
 *    italic, underline, lists, tables, links, images, embedded video,
 *    block quotes and code blocks.
 */

export const RICH_TEXT_ALLOWED_TAGS = [
  'p', 'br', 'hr', 'span', 'div',
  'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
  'strong', 'b', 'em', 'i', 'u', 's', 'sub', 'sup', 'mark', 'small',
  'ul', 'ol', 'li',
  'blockquote', 'pre', 'code',
  'a', 'img', 'figure', 'figcaption',
  'table', 'thead', 'tbody', 'tfoot', 'tr', 'th', 'td', 'caption',
  'iframe', 'video', 'source',
];

export const RICH_TEXT_ALLOWED_ATTR = [
  'href', 'target', 'rel', 'title',
  'src', 'alt', 'width', 'height', 'loading',
  'colspan', 'rowspan', 'scope',
  'class', 'style',
  'allow', 'allowfullscreen', 'frameborder', 'controls',
];

/** The single sanitiser used by both the editor and the renderer. */
export function sanitiseRichText(html: string): string {
  if (!html) return '';
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS: RICH_TEXT_ALLOWED_TAGS,
    ALLOWED_ATTR: RICH_TEXT_ALLOWED_ATTR,
    ALLOW_DATA_ATTR: false,
    ADD_ATTR: ['target'],
  });
}

/** True when the HTML has no visible content (used for fallback decisions). */
export function isRichTextEmpty(html: string | undefined | null): boolean {
  if (!html) return true;
  const text = html
    .replace(/<(script|style)[^>]*>[\s\S]*?<\/\1>/gi, '')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/g, ' ')
    .trim();
  if (text) return false;
  // Content may be media-only.
  return !/<(img|iframe|video|table)\b/i.test(html);
}

/** Plain-text preview for list rows and search. */
export function richTextToPlain(html: string | undefined | null, maxLength = 240): string {
  if (!html) return '';
  const text = html
    .replace(/<(script|style)[^>]*>[\s\S]*?<\/\1>/gi, '')
    .replace(/<\/(p|div|li|h[1-6]|tr)>/gi, ' ')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  return text.length > maxLength ? `${text.slice(0, maxLength - 1)}…` : text;
}

/* ─── Toolbar ─────────────────────────────────────────────────────────────── */

interface ToolbarButtonProps {
  label: string;
  onAction: () => void;
  children: React.ReactNode;
  active?: boolean;
}

function ToolbarButton({ label, onAction, children, active }: ToolbarButtonProps) {
  return (
    <button
      type="button"
      title={label}
      aria-label={label}
      aria-pressed={active}
      // onMouseDown + preventDefault keeps the caret/selection inside the editor.
      onMouseDown={(event) => {
        event.preventDefault();
        onAction();
      }}
      className={`inline-flex h-8 w-8 items-center justify-center rounded-lg border border-transparent text-slate-500 transition hover:bg-slate-100 hover:text-slate-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary/40 ${
        active ? 'bg-brand-active text-brand' : ''
      }`}
    >
      {children}
    </button>
  );
}

function Divider() {
  return <span className="mx-1 h-5 w-px bg-slate-200" aria-hidden="true" />;
}

/* ─── Editor ──────────────────────────────────────────────────────────────── */

export interface RichTextEditorProps {
  value: string;
  onChange: (html: string) => void;
  placeholder?: string;
  minHeight?: number;
  disabled?: boolean;
  /** Hide table / media controls for short fields. */
  compact?: boolean;
  ariaLabel?: string;
  /** Supply to enable image upload; must resolve to a public URL. */
  onUploadImage?: (file: File) => Promise<string>;
}

export function RichTextEditor({
  value,
  onChange,
  placeholder = 'Start typing…',
  minHeight = 180,
  disabled = false,
  compact = false,
  ariaLabel = 'Rich text editor',
  onUploadImage,
}: RichTextEditorProps) {
  const editorRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const lastEmitted = useRef(value);
  const [focused, setFocused] = useState(false);
  const [uploading, setUploading] = useState(false);

  /* Only write into the DOM when the incoming value differs from what the user
     typed — otherwise React would reset the caret on every keystroke. */
  useEffect(() => {
    const el = editorRef.current;
    if (!el) return;
    if (value !== lastEmitted.current) {
      el.innerHTML = sanitiseRichText(value || '');
      lastEmitted.current = value;
    }
  }, [value]);

  useEffect(() => {
    const el = editorRef.current;
    if (el && !el.innerHTML && value) {
      el.innerHTML = sanitiseRichText(value);
      lastEmitted.current = value;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const emit = useCallback(() => {
    const el = editorRef.current;
    if (!el) return;
    const html = sanitiseRichText(el.innerHTML);
    lastEmitted.current = html;
    onChange(html);
  }, [onChange]);

  const exec = useCallback(
    (command: string, argument?: string) => {
      if (disabled) return;
      editorRef.current?.focus();
      // execCommand is deprecated but remains the only cross-browser way to do
      // this without pulling in an editor framework.
      document.execCommand(command, false, argument);
      emit();
    },
    [disabled, emit]
  );

  const insertHtml = useCallback(
    (html: string) => {
      if (disabled) return;
      editorRef.current?.focus();
      document.execCommand('insertHTML', false, html);
      emit();
    },
    [disabled, emit]
  );

  const handleLink = async () => {
    const url = await promptDialog('Link URL (https://…)');
    if (!url) return;
    const safe = /^(https?:|mailto:|tel:)/i.test(url) ? url : `https://${url}`;
    exec('createLink', safe);
    // Force links to open safely.
    const el = editorRef.current;
    el?.querySelectorAll('a').forEach((anchor) => {
      anchor.setAttribute('target', '_blank');
      anchor.setAttribute('rel', 'noreferrer noopener');
    });
    emit();
  };

  const handleTable = async () => {
    const rows = Number((await promptDialog('Number of rows', '3')) || 0);
    const cols = Number((await promptDialog('Number of columns', '3')) || 0);
    if (!rows || !cols) return;
    const header = `<tr>${Array.from({ length: cols }, (_, i) => `<th>Column ${i + 1}</th>`).join('')}</tr>`;
    const body = Array.from(
      { length: Math.max(0, rows - 1) },
      () => `<tr>${Array.from({ length: cols }, () => '<td>&nbsp;</td>').join('')}</tr>`
    ).join('');
    insertHtml(`<table class="rte-table"><thead>${header}</thead><tbody>${body}</tbody></table><p><br/></p>`);
  };

  const handleVideo = async () => {
    const url = await promptDialog('Video URL (YouTube, Vimeo, Loom or direct MP4)');
    if (!url) return;
    const youtube = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([\w-]{6,})/i);
    const vimeo = url.match(/vimeo\.com\/(\d+)/i);
    const loom = url.match(/loom\.com\/share\/([\w-]+)/i);
    let embed = '';
    if (youtube) embed = `https://www.youtube.com/embed/${youtube[1]}`;
    else if (vimeo) embed = `https://player.vimeo.com/video/${vimeo[1]}`;
    else if (loom) embed = `https://www.loom.com/embed/${loom[1]}`;

    if (embed) {
      insertHtml(
        `<figure class="rte-embed"><iframe src="${embed}" width="560" height="315" frameborder="0" allowfullscreen loading="lazy"></iframe></figure><p><br/></p>`
      );
    } else if (/^https?:\/\//i.test(url)) {
      insertHtml(`<video src="${url}" controls width="560"></video><p><br/></p>`);
    }
  };

  const handleImage = async (file: File) => {
    if (!onUploadImage) return;
    setUploading(true);
    try {
      const url = await onUploadImage(file);
      if (url) insertHtml(`<img src="${url}" alt="${file.name}" loading="lazy" />`);
    } catch (err) {
      console.error('[RichTextEditor] image upload failed:', err);
      notifyToast('error', 'That image could not be uploaded.');
    } finally {
      setUploading(false);
    }
  };

  const handleImageButton = async () => {
    if (onUploadImage) {
      fileInputRef.current?.click();
      return;
    }
    const url = await promptDialog('Image URL (https://…)');
    if (url) insertHtml(`<img src="${url}" alt="" loading="lazy" />`);
  };

  /* Paste as plain text so external styles never leak into stored content. */
  const handlePaste = (event: React.ClipboardEvent<HTMLDivElement>) => {
    const html = event.clipboardData.getData('text/html');
    if (!html) return;
    event.preventDefault();
    document.execCommand('insertHTML', false, sanitiseRichText(html));
    emit();
  };

  return (
    <div
      className={`rounded-xl border bg-white transition ${
        focused ? 'border-blue-400 ring-2 ring-blue-500/15' : 'border-slate-200'
      } ${disabled ? 'opacity-60' : ''}`}
    >
      <div
        className="flex flex-wrap items-center gap-0.5 border-b border-slate-100 px-2 py-1.5"
        role="toolbar"
        aria-label="Formatting"
      >
        <ToolbarButton label="Heading 1" onAction={() => exec('formatBlock', '<h1>')}>
          <Heading1 className="h-4 w-4" />
        </ToolbarButton>
        <ToolbarButton label="Heading 2" onAction={() => exec('formatBlock', '<h2>')}>
          <Heading2 className="h-4 w-4" />
        </ToolbarButton>
        <ToolbarButton label="Heading 3" onAction={() => exec('formatBlock', '<h3>')}>
          <Heading3 className="h-4 w-4" />
        </ToolbarButton>
        <Divider />
        <ToolbarButton label="Bold" onAction={() => exec('bold')}>
          <Bold className="h-4 w-4" />
        </ToolbarButton>
        <ToolbarButton label="Italic" onAction={() => exec('italic')}>
          <Italic className="h-4 w-4" />
        </ToolbarButton>
        <ToolbarButton label="Underline" onAction={() => exec('underline')}>
          <Underline className="h-4 w-4" />
        </ToolbarButton>
        <Divider />
        <ToolbarButton label="Bulleted list" onAction={() => exec('insertUnorderedList')}>
          <List className="h-4 w-4" />
        </ToolbarButton>
        <ToolbarButton label="Numbered list" onAction={() => exec('insertOrderedList')}>
          <ListOrdered className="h-4 w-4" />
        </ToolbarButton>
        <ToolbarButton label="Block quote" onAction={() => exec('formatBlock', '<blockquote>')}>
          <Quote className="h-4 w-4" />
        </ToolbarButton>
        <ToolbarButton label="Code block" onAction={() => exec('formatBlock', '<pre>')}>
          <Code className="h-4 w-4" />
        </ToolbarButton>
        <Divider />
        <ToolbarButton label="Insert link" onAction={handleLink}>
          <Link2 className="h-4 w-4" />
        </ToolbarButton>
        {!compact && (
          <>
            <ToolbarButton label={uploading ? 'Uploading…' : 'Insert image'} onAction={handleImageButton}>
              <ImageIcon className="h-4 w-4" />
            </ToolbarButton>
            <ToolbarButton label="Embed video" onAction={handleVideo}>
              <Video className="h-4 w-4" />
            </ToolbarButton>
            <ToolbarButton label="Insert table" onAction={handleTable}>
              <TableIcon className="h-4 w-4" />
            </ToolbarButton>
          </>
        )}
        <Divider />
        <ToolbarButton label="Clear formatting" onAction={() => exec('removeFormat')}>
          <RemoveFormatting className="h-4 w-4" />
        </ToolbarButton>
        <ToolbarButton label="Undo" onAction={() => exec('undo')}>
          <Undo2 className="h-4 w-4" />
        </ToolbarButton>
        <ToolbarButton label="Redo" onAction={() => exec('redo')}>
          <Redo2 className="h-4 w-4" />
        </ToolbarButton>
      </div>

      <div
        ref={editorRef}
        className="rte-content px-3.5 py-3 text-sm text-slate-800 focus:outline-none focus:ring-[3px] focus:ring-brand-primary/15"
        style={{ minHeight }}
        contentEditable={!disabled}
        suppressContentEditableWarning
        role="textbox"
        aria-multiline="true"
        aria-label={ariaLabel}
        data-placeholder={placeholder}
        onInput={emit}
        onBlur={() => {
          setFocused(false);
          emit();
        }}
        onFocus={() => setFocused(true)}
        onPaste={handlePaste}
      />

      {onUploadImage && (
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={(event) => {
            const file = event.target.files?.[0];
            if (file) void handleImage(file);
            event.target.value = '';
          }}
        />
      )}
    </div>
  );
}

/* ─── Renderer ────────────────────────────────────────────────────────────── */

export interface RichTextContentProps {
  html: string | undefined | null;
  className?: string;
  /** Rendered when the content is empty. */
  fallback?: React.ReactNode;
}

/**
 * The ONLY approved way to render stored program content in a portal.
 * Sanitises again at render time, so content written before the allow-list
 * existed cannot execute.
 */
export function RichTextContent({ html, className = '', fallback = null }: RichTextContentProps) {
  if (isRichTextEmpty(html)) return <>{fallback}</>;
  return (
    <div
      className={`rte-content ${className}`.trim()}
      dangerouslySetInnerHTML={{ __html: sanitiseRichText(html || '') }}
    />
  );
}

export default RichTextEditor;
